import './Toast.scss'
import { Toast as BootstrapToast } from 'react-bootstrap'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import * as Sentry from '@sentry/react'
import { type ToastProps as BootstrapToastProps } from 'react-bootstrap/Toast'
import { usePausableAutoHide } from '../../common/usePausableAutoHide'

export type ToastVariant = 'error' | 'warning' | 'info' | 'success'

const DEFAULT_TOAST_DELAY_MS = 3000
const ERROR_TOAST_DELAY_MS = 8000

export interface ToastProps extends BootstrapToastProps {
  variant?: ToastVariant
  delay?: number
}

export const Toast: React.FC<ToastProps> = ({ variant, title, delay, autohide = true, children, onClose, ...rest }) => {
  const bodyRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [isHovered, setIsHovered] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const isPaused = isHovered || isFocused
  const [hasMoreBelow, setHasMoreBelow] = useState(false)
  const [copied, setCopied] = useState(false)

  const isError = variant === 'error'
  const totalDelay = delay ?? (isError ? ERROR_TOAST_DELAY_MS : DEFAULT_TOAST_DELAY_MS)

  const updateHasMoreBelow = useCallback(() => {
    const el = scrollRef.current
    setHasMoreBelow(el ? el.scrollHeight - el.scrollTop - el.clientHeight > 1 : false)
  }, [])

  // Re-checks for hidden content whenever the body or its content changes size; the observer also fires once on mount
  useEffect(() => {
    if (!scrollRef.current || !bodyRef.current || typeof ResizeObserver === 'undefined') {
      return
    }
    const observer = new ResizeObserver(updateHasMoreBelow)
    observer.observe(scrollRef.current)
    observer.observe(bodyRef.current)
    return () => observer.disconnect()
  }, [children, updateHasMoreBelow])

  usePausableAutoHide({ durationMs: autohide ? totalDelay : undefined, isPaused, onExpire: () => onClose?.() })

  const variantClassMap: { [key: string]: string } = {
    error: 'toast toast-error',
    warning: 'toast toast-warning',
    info: 'toast toast-info',
    success: 'toast toast-success',
  }

  const variantIconMap = {
    error: 'bi-x-circle-fill',
    warning: 'bi-exclamation-triangle-fill',
    info: 'bi-info-circle-fill',
    success: 'bi-check-circle-fill',
  }

  const displayTitle = title ? title : 'Something went wrong!'

  const handleCopy = async () => {
    // innerText keeps the line breaks between list items; textContent covers environments without layout (jsdom)
    const body = bodyRef.current?.innerText ?? bodyRef.current?.textContent
    const text = [displayTitle, body?.trim()].filter(Boolean).join(': ')
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  return (
    <BootstrapToast
      show={true}
      className={variantClassMap[variant || 'info']}
      onClose={onClose}
      autohide={false}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={(event) => {
        // Moving focus between the toast's own buttons shouldn't restart the countdown
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setIsFocused(false)
        }
      }}
      {...rest}
    >
      <div className={classNames('d-flex justify-content-between gap-2', { 'pb-2': children })}>
        <div className='toast-header-content flex-grow-1'>
          <div className='d-flex gap-2'>
            <i className={classNames('bi toast-status-icon', variantIconMap[variant || 'info'])} />
            <div className='paragraph-medium fw-bold text-break toast-header-content'>{displayTitle}</div>
          </div>
        </div>
        <div className='d-flex gap-2 align-items-center flex-shrink-0'>
          {isError ? (
            <button
              type='button'
              className='border-0 bg-transparent p-0 text-reset lh-1'
              aria-label={copied ? 'Copied' : 'Copy error'}
              title={copied ? 'Copied' : 'Copy'}
              onClick={handleCopy}
            >
              <i className={classNames('bi', copied ? 'bi-check' : 'bi-copy')} />
            </button>
          ) : null}
          <button type='button' className='border-0 bg-transparent p-0 text-reset lh-1' aria-label='Close' onClick={() => onClose && onClose()}>
            <i className='bi bi-x fs-5 lh-1' />
          </button>
        </div>
      </div>
      {children ? (
        <>
          <div className='border-top' />
          <div ref={scrollRef} className={classNames('pt-2 toast-body-scroll', { 'toast-body-scroll-more': hasMoreBelow })} onScroll={updateHasMoreBelow}>
            <div ref={bodyRef}>{children}</div>
          </div>
        </>
      ) : null}
    </BootstrapToast>
  )
}
